import {
  Download, Filter,
  ChevronLeft, ChevronRight, Wallet, Clock,
  TrendingUp, Calendar, Building2, FileText,
  RefreshCw, CheckCheck, X, ArrowUpRight
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL as API } from '../../config/api';

export default function PartnerPayouts({ data }) {
  const { token } = useAuth();
  const [withdrawals, setWithdrawals]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [dateFilter, setDateFilter]     = useState('All Dates');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [methodFilter, setMethodFilter] = useState('All Payout Methods');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [page, setPage]                 = useState(1);
  const PER_PAGE = 8;

  const fetchWithdrawals = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/partner/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWithdrawals(res.data || []);
    } catch (err) {
      console.error('Failed to fetch partner payouts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadInvoice = async (txnId, invoiceNo) => {
    setDownloadingId(txnId);
    try {
      const res = await axios.get(`${API}/wallet/payouts/${txnId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TurfX_Payout_Invoice_${invoiceNo || txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      alert('Failed to download payout invoice PDF. Invoice is generated once admin approves the payout.');
    } finally {
      setDownloadingId(null);
    }
  };

  const today = new Date();
  const monthLabel = today.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  // Payout items mapped from real withdrawal transactions
  const payoutItems = withdrawals.map(w => {
    const meta = w.metadata || {};
    const isCompleted = w.status === 'success';
    const isPending = w.status === 'pending';
    const method = meta.payment_mode || (meta.upiId ? 'UPI Transfer' : 'Bank Transfer');
    const ref = meta.transaction_id || meta.razorpay_utr || meta.razorpay_payout_id || w.referenceId || (isCompleted ? `TRX-${w.id.slice(0, 8).toUpperCase()}` : 'Awaiting admin approval');

    return {
      raw: w,
      id: w.id,
      shortId: `PO-${w.id.slice(0, 8).toUpperCase()}`,
      date: w.createdAt ? new Date(w.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
      time: w.createdAt ? new Date(w.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
      amount: w.amount,
      method,
      bank: meta.bankName || (meta.upiId ? `UPI: ${meta.upiId}` : 'Bank Account'),
      status: isCompleted ? 'Completed' : isPending ? 'Processing' : 'Failed',
      ref,
      invoiceNumber: meta.invoice_number || (isCompleted ? `INV-PAY-${w.id.slice(0, 8).toUpperCase()}` : null),
      metadata: meta,
      createdAt: w.createdAt,
    };
  });

  function parseDateParts(dateVal) {
    if (!dateVal) return null;
    if (typeof dateVal === 'string') {
      const clean = dateVal.trim();
      const isoMatch = clean.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return {
          year: parseInt(isoMatch[1], 10),
          month: parseInt(isoMatch[2], 10),
          day: parseInt(isoMatch[3], 10),
        };
      }
    }
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return null;
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
  }

  // Apply filters to payouts
  const filteredPayouts = payoutItems.filter(p => {
    const matchStatus = statusFilter === 'All Status' || p.status === statusFilter;
    const matchMethod = methodFilter === 'All Payout Methods' || p.method === methodFilter;
    let matchDate = true;
    const parts = parseDateParts(p.createdAt || p.raw?.created_at || p.date);
    if (dateFilter === 'This Week') {
      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
      matchDate = new Date(p.createdAt || p.date) >= weekAgo;
    } else if (dateFilter === 'This Month') {
      matchDate = !!parts && parts.year === today.getFullYear() && parts.month === (today.getMonth() + 1);
    } else if (dateFilter === 'Last Month') {
      const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const lastMonth = today.getMonth() === 0 ? 12 : today.getMonth();
      matchDate = !!parts && parts.year === lastMonthYear && parts.month === lastMonth;
    }
    return matchStatus && matchMethod && matchDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredPayouts.length / PER_PAGE));
  const pagedPayouts = filteredPayouts.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const totalCompleted = payoutItems
    .filter(p => p.status === 'Completed')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const totalPending = payoutItems
    .filter(p => p.status === 'Processing')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const thisMonthTotal = payoutItems
    .filter(p => {
      if (p.status !== 'Completed') return false;
      const parts = parseDateParts(p.createdAt || p.raw?.created_at || p.date);
      return !!parts && parts.year === today.getFullYear() && parts.month === (today.getMonth() + 1);
    })
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const getBookingEarnings = (b) => {
    if (typeof b?.partner_payout === 'number') return b.partner_payout;
    if (typeof b?.partnerPayout === 'number') return b.partnerPayout;
    const base = b?.base_price ?? b?.total_price ?? 0;
    return Math.round(base * 0.95);
  };

  const validBookings = (data?.bookings || []).filter(b => 
    ['confirmed', 'completed', 'checked-in', 'checkedin', 'approved', 'active', 'success'].includes((b.status || '').toLowerCase())
  );
  const totalPartnerEarnings = validBookings.reduce((sum, b) => sum + getBookingEarnings(b), 0);
  const totalEarningsDisplay = typeof data?.totalEarnings === 'number' && data.totalEarnings > 0
    ? data.totalEarnings
    : totalPartnerEarnings;

  const stats = [
    { label: 'Settled Payouts', value: `₹${Math.round(totalCompleted).toLocaleString('en-IN')}`, icon: <CheckCheck size={22} />, color: '#10b981', sub: `${payoutItems.filter(p => p.status === 'Completed').length} Disbursed` },
    { label: 'Pending Approval', value: `₹${Math.round(totalPending).toLocaleString('en-IN')}`, icon: <Clock size={22} />, color: '#f59e0b', sub: `${payoutItems.filter(p => p.status === 'Processing').length} In Review` },
    { label: 'This Month Paid', value: `₹${Math.round(thisMonthTotal).toLocaleString('en-IN')}`, icon: <Calendar size={22} />, color: '#8b5cf6', sub: monthLabel },
    { label: 'Total Partner Earnings', value: `₹${Math.round(totalEarningsDisplay).toLocaleString('en-IN')}`, icon: <TrendingUp size={22} />, color: '#3b82f6', sub: 'Net Partner Payouts' },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>Payouts & Invoices</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>
            Official settlement disbursements and approved tax invoices from TurfX
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchWithdrawals} style={btnSecondary}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/exports/payouts/csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `payouts-${Date.now()}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch { alert('Download failed. Please try again.'); }
            }}
            style={btnPrimary}
          >
            <Download size={18} /> Download Statement (CSV)
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: `${s.color}15`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#161616' }}>{s.value}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#111827' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="table-responsive" style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', overflowX: 'auto' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1.5px solid #EEF2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
           <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <select value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} style={filterSelect}>
                <option>All Dates</option>
                <option>This Week</option>
                <option>This Month</option>
                <option>Last Month</option>
              </select>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={filterSelect}>
                <option>All Status</option>
                <option>Completed</option>
                <option>Processing</option>
              </select>
              <select value={methodFilter} onChange={e => { setMethodFilter(e.target.value); setPage(1); }} style={filterSelect}>
                <option>All Payout Methods</option>
                <option>UPI Transfer</option>
                <option>Bank Transfer</option>
              </select>
           </div>
           <button
             onClick={() => { setDateFilter('All Dates'); setStatusFilter('All Status'); setMethodFilter('All Payout Methods'); setPage(1); }}
             style={filterBtn}
           >
             <Filter size={18} /> Clear Filters
           </button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6' }}>
            <tr>
              <th style={thStyle}>Payout Reference</th>
              <th style={thStyle}>Request Date</th>
              <th style={thStyle}>Amount</th>
              <th style={thStyle}>Payout Method</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>UTR / Reference ID</th>
              <th style={thStyle}>Invoice (PDF)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#374151', fontWeight: '700', padding: '3rem' }}>
                  Loading payouts & invoices...
                </td>
              </tr>
            ) : pagedPayouts.length > 0 ? pagedPayouts.map((p) => (
              <tr 
                key={p.id} 
                onClick={() => setSelectedPayout(p)}
                style={{ borderBottom: '1px solid #EEF2E6', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: '800', fontSize: '0.85rem', color: '#084734' }}>{p.shortId}</div>
                  {p.invoiceNumber && (
                    <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: '700', marginTop: '2px' }}>
                      {p.invoiceNumber}
                    </div>
                  )}
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#161616' }}>{p.date}</div>
                  <div style={{ fontSize: '0.75rem', color: '#4B5563' }}>{p.time}</div>
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: '900', fontSize: '0.95rem', color: '#084734' }}>
                    ₹{p.amount.toLocaleString()}
                  </div>
                </td>
                <td style={tdStyle}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Building2 size={18} color="#084734" />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '0.85rem' }}>{p.method}</div>
                        <div style={{ fontSize: '0.75rem', color: '#4B5563' }}>{p.bank}</div>
                      </div>
                   </div>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '4px 12px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '800',
                    background: p.status === 'Completed' ? '#DCEFB8' : p.status === 'Processing' ? '#FEF3C7' : '#FEE2E2',
                    color: p.status === 'Completed' ? '#084734' : p.status === 'Processing' ? '#92400E' : '#DC2626'
                  }}>{p.status.toUpperCase()}</span>
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: '600', fontSize: '0.8rem', color: '#374151', fontFamily: 'monospace' }}>
                    {p.ref}
                  </div>
                </td>
                <td style={tdStyle}>
                  {p.status === 'Completed' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadInvoice(p.id, p.invoiceNumber);
                      }}
                      disabled={downloadingId === p.id}
                      style={btnInvoice}
                      title="Download Payout Invoice PDF"
                    >
                      <FileText size={14} /> {downloadingId === p.id ? 'PDF...' : 'Invoice PDF'}
                    </button>
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>Pending Approval</span>
                  )}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#374151', fontWeight: '700', padding: '3rem' }}>
                  No payout requests or invoices found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ padding: '1.2rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAF7' }}>
           <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600' }}>
             Showing {filteredPayouts.length} payout record{filteredPayouts.length === 1 ? '' : 's'}
           </div>
           <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ ...pageBtn, opacity: page === 1 ? 0.4 : 1 }}
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{ ...pageBtn, background: page === p ? '#CEF17B' : 'white', color: page === p ? '#084734' : '#374151', border: page === p ? 'none' : '1.5px solid #EEF2E6', fontWeight: page === p ? '900' : '700' }}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ ...pageBtn, opacity: page === totalPages ? 0.4 : 1 }}
              >
                <ChevronRight size={16} />
              </button>
           </div>
        </div>
      </div>

      {/* Payout & Invoice Details Modal */}
      {selectedPayout && (
        <div style={modalOverlay} onClick={() => setSelectedPayout(null)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616', margin: 0 }}>Payout & Invoice Details</h3>
              <X size={20} onClick={() => setSelectedPayout(null)} style={{ cursor: 'pointer', color: '#374151' }} />
            </div>

            {/* Top card */}
            <div style={{ textAlign: 'center', padding: '1.25rem', background: '#F8FAF7', borderRadius: '16px', marginBottom: '1.5rem', border: '1px solid #EEF2E6' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', color: '#374151', marginBottom: '4px', letterSpacing: '0.5px' }}>
                Disbursed Settlement Amount
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#084734' }}>
                ₹{selectedPayout.amount.toLocaleString()}
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{
                  background: selectedPayout.status === 'Completed' ? '#D1FAE5' : selectedPayout.status === 'Processing' ? '#FEF3C7' : '#FEE2E2',
                  color: selectedPayout.status === 'Completed' ? '#065F46' : selectedPayout.status === 'Processing' ? '#92400E' : '#DC2626',
                  padding: '4px 12px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase'
                }}>
                  {selectedPayout.status}
                </span>
              </div>
            </div>

            {/* Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <div style={detailRow}>
                <span style={detailLabel}>Payout Reference ID</span>
                <span style={{ ...detailValue, fontFamily: 'monospace' }}>{selectedPayout.id}</span>
              </div>
              {selectedPayout.invoiceNumber && (
                <div style={detailRow}>
                  <span style={detailLabel}>Tax Invoice Number</span>
                  <span style={{ ...detailValue, color: '#16a34a', fontWeight: '800', fontFamily: 'monospace' }}>
                    {selectedPayout.invoiceNumber}
                  </span>
                </div>
              )}
              <div style={detailRow}>
                <span style={detailLabel}>Request Date & Time</span>
                <span style={detailValue}>{selectedPayout.date} {selectedPayout.time}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Payment Method</span>
                <span style={{ ...detailValue, color: '#084734', fontWeight: '800' }}>{selectedPayout.method}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Bank / Settlement Target</span>
                <span style={detailValue}>{selectedPayout.bank}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Bank UTR / Transaction Ref</span>
                <span style={{ ...detailValue, fontFamily: 'monospace' }}>{selectedPayout.ref}</span>
              </div>
              {selectedPayout.metadata?.accountHolderName && (
                <div style={detailRow}>
                  <span style={detailLabel}>Account Holder</span>
                  <span style={detailValue}>{selectedPayout.metadata.accountHolderName}</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              {selectedPayout.status === 'Completed' && (
                <button
                  onClick={() => downloadInvoice(selectedPayout.id, selectedPayout.invoiceNumber)}
                  disabled={downloadingId === selectedPayout.id}
                  style={{ ...btnPrimary, width: '100%', justifyContent: 'center', padding: '12px' }}
                >
                  <FileText size={16} /> {downloadingId === selectedPayout.id ? 'Generating PDF...' : 'Download Official Invoice (PDF)'}
                </button>
              )}
              <button onClick={() => setSelectedPayout(null)} style={{ ...btnSecondary, width: '100%', justifyContent: 'center', padding: '10px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const statCard = { background: 'white', padding: '1.5rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' };
const btnPrimary = { background: '#CEF17B', color: '#084734', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(8,71,52,0.1)' };
const btnSecondary = { background: 'white', color: '#374151', border: '1.5px solid #EEF2E6', padding: '10px 18px', borderRadius: '12px', fontWeight: '700', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const btnInvoice = { background: '#F0FDF4', color: '#16a34a', border: '1.5px solid #86efac', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' };
const filterSelect = { padding: '10px 15px', borderRadius: '12px', border: '1.5px solid #EEF2E6', fontSize: '0.85rem', fontWeight: '600', color: '#161616', outline: 'none', background: 'white' };
const filterBtn = { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '12px', border: '1.5px solid #EEF2E6', fontSize: '0.85rem', fontWeight: '600', color: '#374151', cursor: 'pointer', background: 'white' };
const thStyle = { textAlign: 'left', padding: '1.2rem', fontSize: '0.75rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '1.2rem', verticalAlign: 'middle' };
const pageBtn = { width: '32px', height: '32px', borderRadius: '8px', border: '1.5px solid #EEF2E6', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modalBox   = { background: 'white', borderRadius: '20px', padding: '2rem', width: '480px', maxWidth: '95%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const detailRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid #F3F4F6',
};
const detailLabel = {
  color: '#374151',
  fontWeight: '600',
};
const detailValue = {
  color: '#161616',
  fontWeight: '700',
  textAlign: 'right',
};
